import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MediaPlayer, MediaProvider, isYouTubeProvider, } from '@vidstack/react';
// Try to use Vidstack default video layout
import { DefaultVideoLayout, defaultLayoutIcons } from '@vidstack/react/player/layouts/default';
// Vidstack styles
import '@vidstack/react/player/styles/base.css';
import '@vidstack/react/player/styles/default/theme.css';
import '@vidstack/react/player/styles/default/layouts/video.css';
const isYouTubeUrl = (url) => /youtube\.com|youtu\.be/.test(url);
const isHlsUrl = (url) => /\.m3u8(\?.*)?$/i.test(url);
const mimeFromUrl = (url) => {
    if (/\.mp4(\?.*)?$/i.test(url))
        return 'video/mp4';
    if (/\.webm(\?.*)?$/i.test(url))
        return 'video/webm';
    if (isHlsUrl(url))
        return 'application/x-mpegURL';
    return undefined;
};
const VidStackPlayer = ({ playerConfig, sourceConfig, onStart, onProgress, onEnded, disableSeek, width, height, initialTime = 0, isLoadingPosition = false, }) => {
    const player = useRef(null);
    const [hasStarted, setHasStarted] = useState(false);
    const [duration, setDuration] = useState(0);
    const [lastTime, setLastTime] = useState(0);
    const [hasSetInitialTime, setHasSetInitialTime] = useState(false);
    const url = playerConfig?.url || '';
    const autoPlay = !!playerConfig?.playing;
    const muted = !!playerConfig?.muted;
    const showControls = playerConfig?.controls !== false;
    const poster = typeof playerConfig?.light === 'string' ? playerConfig.light : undefined;
    const dataAttrs = useMemo(() => {
        const out = {};
        if (playerConfig) {
            Object.keys(playerConfig).forEach((k) => {
                if (k.startsWith('data-'))
                    out[k] = playerConfig[k];
            });
        }
        return out;
    }, [playerConfig]);
    const wantsHls = !!sourceConfig?.file?.forceHLS || isHlsUrl(url) || url.startsWith('rtmp://');
    // Build src for Vidstack
    const src = useMemo(() => {
        if (!url)
            return undefined;
        if (isYouTubeUrl(url)) {
            return url; // Vidstack can infer YouTube provider from URL
        }
        if (wantsHls) {
            return { src: url, type: 'application/x-mpegURL' };
        }
        const type = mimeFromUrl(url);
        return type ? { src: url, type } : url;
    }, [url, wantsHls]);
    const streamType = useMemo(() => {
        // Treat HLS or rtmp as live if "/live" or explicit HLS and not file extension known
        if (wantsHls && /live|broadcast/i.test(url))
            return 'live';
        return 'on-demand';
    }, [url, wantsHls]);
    useEffect(() => {
        if (!hasSetInitialTime && !isLoadingPosition && initialTime > 0 && player.current && duration > 0) {
            player.current.currentTime = initialTime;
            setLastTime(initialTime);
            setHasSetInitialTime(true);
        }
    }, [initialTime, isLoadingPosition, hasSetInitialTime, duration]);
    const computeProgress = useCallback((currentTime) => {
        const dur = duration || player.current?.duration || 0;
        const played = dur > 0 ? currentTime / dur : 0;
        return {
            played,
            playedSeconds: currentTime,
            loaded: 0,
            loadedSeconds: 0,
        };
    }, [duration]);
    const handleProviderChange = (provider) => {
        if (isYouTubeProvider(provider)) {
            provider.cookies = true;
        }
    };
    const handleTimeUpdate = () => {
        const ct = player.current?.currentTime ?? 0;
        if (ct > lastTime)
            setLastTime(ct);
        onProgress(computeProgress(ct));
    };
    const handleLoadedMetadata = () => {
        const dur = player.current?.duration ?? 0;
        if (dur)
            setDuration(dur);
        if (!hasSetInitialTime && !isLoadingPosition && initialTime > 0 && player.current) {
            player.current.currentTime = initialTime;
            setLastTime(initialTime);
            setHasSetInitialTime(true);
        }
    };
    const handlePlay = () => {
        if (!hasStarted) {
            setHasStarted(true);
            onStart();
        }
    };
    const handleEnd = () => {
        onEnded();
        try {
            if (typeof (playerConfig?.onEnded) === 'function') {
                playerConfig.onEnded();
            }
        }
        catch (e) {
            // ignore
        }
    };
    const handleSeeking = () => {
        if (!disableSeek)
            return;
        const ct = player.current?.currentTime ?? 0;
        // Prevent any seek away from the last known time.
        if (Math.abs(ct - lastTime) > 0.5) {
            if (player.current)
                player.current.currentTime = lastTime;
        }
    };
    const handleKeyDown = (e) => {
        if (!disableSeek)
            return;
        const codes = ['ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'];
        if (codes.includes(e.key)) {
            e.preventDefault();
            e.stopPropagation();
        }
    };
    // Disable timeline interactions if seeking is locked
    const layoutClass = disableSeek ? 'pointer-events-auto [&_.plyr__progress]:pointer-events-none' : '';
    return (<div className="w-full" style={{ width: width || '100%', height: height || 'auto' }} {...dataAttrs}>
            <MediaPlayer className="w-full h-full aspect-video bg-slate-900 text-white font-sans overflow-hidden rounded-md" title={playerConfig?.chapters?.title || 'Video'} aspectRatio="16 / 9" src={src} poster={poster} viewType="video" streamType={streamType} playsInline controls={showControls} muted={muted} autoPlay={autoPlay} ref={player} onProviderChange={handleProviderChange} onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} onPlay={handlePlay} onEnded={handleEnd} onSeeking={handleSeeking} onKeyDown={handleKeyDown}>
                <MediaProvider iframeProps={{ width: '100%', height: '100%' }} style={{ width: '100%', height: '100%' }}/>
                {showControls ? <DefaultVideoLayout className={layoutClass} icons={defaultLayoutIcons}/> : null}
            </MediaPlayer>
        </div>);
};
VidStackPlayer.displayName = 'VidStackPlayer';
export default VidStackPlayer;
